import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Trash2, Inbox } from "lucide-react";
import { Wordmark } from "../sections/Nav";
import { ADMIN_TOKEN_KEY } from "./AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY) || ""}`,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/demo-requests`, { headers: authHeaders() });
      setRequests(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate("/admin/login");
      } else {
        setError("Could not load demo requests. Please try again.");
        setRequests([]);
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const remove = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/demo-requests/${id}`, { headers: authHeaders() });
      setRequests((rs) => rs.filter((r) => r.id !== id));
    } catch {
      setError("Could not delete that request.");
    } finally {
      setDeleting(null);
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-paper font-sans text-ink">
      <header className="border-b border-ink/10 bg-paper">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
          <Link to="/" aria-label="Back to Leadtochair home">
            <Wordmark />
          </Link>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2 text-sm text-ink/70 transition-colors hover:border-teal hover:text-teal"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      </header>

      <main data-testid="admin-dashboard" className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-medium tracking-tight text-ink">
              Demo requests
            </h1>
            <p className="mt-2 text-sm text-ink/55">
              Every Book a Demo submission from the website, newest first.
            </p>
          </div>
          {requests && (
            <span
              data-testid="admin-request-count"
              className="rounded-full bg-teal-soft px-4 py-1.5 text-sm font-medium text-teal"
            >
              {requests.length} {requests.length === 1 ? "request" : "requests"}
            </span>
          )}
        </div>

        {error && (
          <p data-testid="admin-error" className="mt-6 text-sm text-red-600">{error}</p>
        )}

        {requests === null ? (
          <div className="mt-20 flex justify-center">
            <Loader2 size={26} className="animate-spin text-teal" />
          </div>
        ) : requests.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="mt-14 rounded-2xl border border-dashed border-ink/25 p-14 text-center"
          >
            <Inbox size={30} className="mx-auto text-ink/30" />
            <p className="mt-4 text-sm text-ink/55">
              No demo requests yet. New submissions will appear here.
            </p>
          </div>
        ) : (
          <div className="mt-10 space-y-4">
            {requests.map((r, i) => (
              <div
                key={r.id}
                data-testid={`request-row-${i}`}
                className="rounded-2xl border border-ink/10 bg-white p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-medium text-ink">
                      {r.name} <span className="text-ink/40">·</span>{" "}
                      <span className="text-teal">{r.practice_name}</span>
                    </h2>
                    <p className="mt-1.5 text-sm text-ink/60">
                      <a href={`mailto:${r.email}`} className="underline decoration-ink/20 underline-offset-2 hover:text-teal">
                        {r.email}
                      </a>
                      {"  ·  "}
                      <a href={`tel:${r.phone}`} className="underline decoration-ink/20 underline-offset-2 hover:text-teal">
                        {r.phone}
                      </a>
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-ink/45">{formatDate(r.created_at)}</span>
                    <button
                      data-testid={`delete-request-${i}`}
                      onClick={() => remove(r.id)}
                      disabled={deleting === r.id}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-ink/50 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                      aria-label={`Delete request from ${r.name}`}
                    >
                      {deleting === r.id ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Trash2 size={15} />
                      )}
                    </button>
                  </div>
                </div>
                {r.message && (
                  <p className="mt-4 rounded-xl bg-paper px-5 py-3.5 text-sm leading-relaxed text-ink/70">
                    {r.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
